import { defineStore } from 'pinia'
import * as transactionService from '@/services/transactionServiceOld'
import type { TransactionRecord, TransactionItem } from '@/types/Transaction'
import * as transactionFactory from '@/factories/transactionFactory'
import { localForageService } from '@/utils/cache'

export const useTransactionStore = defineStore('transactions', {
  state: () => ({
    transactions: [] as TransactionRecord[],
    currentTransaction: null as TransactionRecord | null,
    loading: false,
    original: null as TransactionRecord | null,
    draft: null as TransactionRecord | null,
    unsubscribe: null as null | (() => void),
    currentItem: transactionFactory.createTransactionItem() as TransactionItem,
  }),
  getters: {
    hasUnsavedChanges(state): boolean {
      if (!state.original || !state.draft) return false
      return JSON.stringify(state.original) !== JSON.stringify(state.draft)
    },
  },
  actions: {
    async startEditing(id: string) {
      this.loading = true
      await transactionService.updateTransaction(id, {
        transaction_status: 'editing',
      })

      const cacheKey = `transaction_draft_${id}`
      const cached = await localForageService.get<TransactionRecord>(cacheKey)

      const original = await transactionService.fetchTransaction(id)
      if (!original) throw new Error('Transaction not found')

      this.original = structuredClone(original)

      // Load draft if exists, otherwise copy original
      const draft = cached ?? structuredClone(original)
      draft.transaction_status = 'editing'
      this.draft = draft

      await localForageService.set(cacheKey, this.draft)
      this.loading = false
    },

    async finalizeTransaction(
      id: string,
      options?: {
        bypassFinalizeRequirements?: boolean
        finalizeBypassReason?: string
      },
    ) {
      // Use v2 TransactionService for finalization
      const { getTransactionService } = await import('@/services/serviceFactory')
      const service = await getTransactionService()
      const updated = await service.finalizeTransaction(id, options)

      // Update local state if needed
      if (this.currentTransaction?.id === id) {
        this.currentTransaction.payment_status = updated.payment_status
        this.currentTransaction.transaction_status = updated.transaction_status
        if (updated.finalizeBypassReason) {
          this.currentTransaction.finalizeBypassReason = updated.finalizeBypassReason
        }
        if (updated.finalizedByUserId) {
          this.currentTransaction.finalizedByUserId = updated.finalizedByUserId
        }
      }

      const index = this.transactions.findIndex((tx) => tx.id === id)
      if (index !== -1) {
        this.transactions[index].payment_status = updated.payment_status
        this.transactions[index].transaction_status = updated.transaction_status
        if (updated.finalizeBypassReason) {
          this.transactions[index].finalizeBypassReason = updated.finalizeBypassReason
        }
        if (updated.finalizedByUserId) {
          this.transactions[index].finalizedByUserId = updated.finalizedByUserId
        }
      }
    },

    async cancelEditing() {
      if (!this.draft || !this.original) return
      const originalStatus = this.original.transaction_status || 'pending'

      // Ensure draft has an ID before proceeding
      if (!this.draft.id) {
        console.error('Cannot cancel editing: draft has no ID')
        return
      }

      await transactionService.updateTransaction(this.draft.id, {
        transaction_status: originalStatus,
      })
      await localForageService.remove(`transaction_draft_${this.draft.id}`)

      this.draft = null
      this.original = null
    },

    async updateDraft(partial: Partial<TransactionRecord>) {
      if (!this.draft) return
      console.log('Updating draft', partial)
      this.draft = { ...this.draft, ...partial }
      await localForageService.set(`transaction_draft_${this.draft.id}`, this.draft)
    },

    async resetDraft() {
      if (!this.original) return
      this.draft = null
      await localForageService.remove(`transaction_draft_${this.original.id}`)
      console.log('Draft reset')
    },

    async saveChanges() {
      if (!this.draft) return
      // Preserve explicit statuses; default to 'pending' when none set
      if (!this.draft.transaction_status || this.draft.transaction_status === 'editing') {
        this.draft.transaction_status = this.draft.payment_status || 'pending'
      }

      // Ensure draft has an ID before proceeding
      if (!this.draft.id) {
        console.error('Cannot save transaction: draft has no ID')
        return
      }

      // Create a copy of the draft with the ID explicitly set as string
      const draftToSave = {
        ...this.draft,
        id: this.draft.id as string,
      }

      // Update Firestore
      await transactionService.updateTransaction(draftToSave.id, draftToSave)

      // Update the transaction in the store's transactions array
      const index = this.transactions.findIndex((tx) => tx.id === draftToSave.id)
      if (index !== -1) {
        this.transactions[index] = draftToSave
      } else {
        // If the transaction isn't in the array yet, add it
        this.transactions.push(draftToSave)
      }

      // Also update currentTransaction if it's the same transaction
      if (this.currentTransaction && this.currentTransaction.id === draftToSave.id) {
        this.currentTransaction = draftToSave
      }

      this.original = null
      await localForageService.remove(`transaction_draft_${draftToSave.id}`)
    },

    async loadTransactions() {
      this.loading = true
      this.transactions = await transactionService.fetchAllTransactions()
      this.loading = false
    },
    async addTransaction(transaction: TransactionRecord) {
      await transactionService.createTransaction(transaction)
    },
    async updateTransaction(id: string, updates: Partial<TransactionRecord>) {
      console.log('Updating transaction:', id, updates)
      await transactionService.updateTransaction(id, updates)

      const index = this.transactions.findIndex((tx) => tx.id === id)
      if (index !== -1) {
        this.transactions[index] = {
          ...this.transactions[index],
          ...updates,
        }
      }

      if (this.draft && this.draft.id === id) {
        this.draft = {
          ...this.draft,
          ...updates,
        }
      }
    },
    async deleteTransaction(id: string) {
      await transactionService.deleteTransaction(id)

      // Remove the transaction from the store's transactions array
      const index = this.transactions.findIndex((tx) => tx.id === id)
      if (index !== -1) {
        this.transactions.splice(index, 1)
      }

      // Clear currentTransaction if it's the deleted transaction
      if (this.currentTransaction && this.currentTransaction.id === id) {
        this.currentTransaction = null
      }

      // Clear draft if it's the deleted transaction
      if (this.draft && this.draft.id === id) {
        this.draft = null
        this.original = null
      }
    },
    async fetchTransaction(id: string) {
      this.loading = true
      try {
        const transaction = await transactionService.fetchTransaction(id)
        this.currentTransaction = transaction
        return transaction
      } finally {
        this.loading = false
      }
    },

    async fetchByPersonId(personId: string) {
      this.loading = true
      try {
        const transactions = await transactionService.fetchTransactionsByPersonId(personId)
        this.transactions = transactions
        return transactions
      } finally {
        this.loading = false
      }
    },

    async countByPersonId(personId: string) {
      return await transactionService.countTransactionsByPersonId(personId)
      // TODO: add localforage caching
    },

    async fetchByOrganizationId(organizationId: string) {
      this.loading = true
      try {
        const transactions =
          await transactionService.fetchTransactionsByOrganizationId(organizationId)
        this.transactions = transactions
        return transactions
      } finally {
        this.loading = false
      }
    },
    async countByOrganizationId(organizationId: string) {
      return await transactionService.countTransactionsByOrganizationId(organizationId)
      // TODO: add localforage caching
    },
    async createNewTransaction() {
      this.loading = true
      try {
        const newTransaction = transactionFactory.createTransaction()
        const docRef = await transactionService.createTransaction(newTransaction)

        const createdTransaction = {
          ...newTransaction,
          id: docRef,
        } as TransactionRecord

        this.draft = createdTransaction

        return createdTransaction
      } finally {
        this.loading = false
      }
    },
    setCurrentItem(item: TransactionItem) {
      this.currentItem = transactionFactory.createTransactionItem(item)
    },

    clearCurrentItem() {
      this.currentItem = transactionFactory.createTransactionItem()
    },

    initNewItem() {
      this.clearCurrentItem()
      return this.currentItem
    },

    editItem(index: number) {
      if (!this.draft || !this.draft.items || index < 0 || index >= this.draft.items.length) {
        return
      }
      console.log('Editing item at index:', index, this.draft.items[index])

      this.currentItem = { ...this.draft.items[index] }
    },

    saveCurrentItem(index: number) {
      if (!this.draft) return

      const newItem = { ...this.currentItem }

      // Initialize items array if it doesn't exist
      if (!this.draft.items) {
        this.draft.items = []
      }

      const updatedItems = [...this.draft.items]

      if (index >= 0 && index < updatedItems.length) {
        // Update existing item
        updatedItems[index] = newItem
      } else {
        // Add new item
        updatedItems.push(newItem)
      }

      // Update the draft with the new items
      this.updateDraft({
        items: updatedItems,
      })

      // Clear the current item
      this.clearCurrentItem()
    },

    removeItem(index: number) {
      if (!this.draft || !this.draft.items || index < 0 || index >= this.draft.items.length) {
        return
      }

      const updatedItems = [...this.draft.items]
      updatedItems.splice(index, 1)

      this.updateDraft({
        items: updatedItems,
      })
    },
  },
})
